import React, { useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Loader2, AlertCircle, ArrowLeft, MailCheck, Building2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function RegisterIndividual() {
  const [form, setForm] = useState({
    email: "", password: "", first_name: "", last_name: "",
    fiscal_code: "", birth_date: "", phone: "", privacy_accepted: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.privacy_accepted) {
      setError("Devi accettare l'informativa privacy.");
      return;
    }
    if (form.password.length < 8) {
      setError("La password deve avere almeno 8 caratteri.");
      return;
    }
    const cf = form.fiscal_code.trim().toUpperCase();
    if (!/^[A-Z0-9]{16}$/.test(cf)) {
      setError("Il codice fiscale è obbligatorio e deve essere di 16 caratteri.");
      return;
    }
    if (!form.birth_date) {
      setError("La data di nascita è obbligatoria.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        fiscal_code: cf,
        phone: form.phone.trim() || null,
      };
      await api.post("/public/register/individual", payload);
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-individual-page" className="min-h-screen bg-brand-bg brand-grid-bg flex flex-col">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4">
          <ArrowLeft size={16} /> Torna alla home
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pb-16">
        <div className="w-full max-w-lg">
          <div className="flex items-center gap-2.5 justify-center mb-8">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-brand-blue text-white">
              <GraduationCap size={22} strokeWidth={2} />
            </span>
            <div className="leading-tight">
              <div className="wordmark text-brand-blue text-xl">Buro Academy</div>
              <div className="text-xs text-brand-muted -mt-0.5">Burocratica Servizi</div>
            </div>
          </div>

          <div className="bg-white border border-brand-line rounded-sm shadow-sm p-8">
            <div className="gold-rule" />

            {done ? (
              <div data-testid="register-individual-done">
                <div className="flex items-center gap-2 text-brand-blue mb-3">
                  <MailCheck size={22} />
                  <h1 className="text-2xl text-brand-ink">Controlla la tua email</h1>
                </div>
                <p className="text-sm text-brand-muted mb-6">
                  Se l'indirizzo non è già registrato, ti abbiamo inviato un'email con
                  il link per confermarlo. Dopo la conferma potrai accedere e acquistare i corsi.
                </p>
                <Link to="/login" className="btn-primary w-full inline-flex items-center justify-center gap-2">
                  Vai all'accesso
                </Link>
              </div>
            ) : (
              <>
                <h1 className="text-2xl text-brand-ink mb-1">Registrati come privato</h1>
                <p className="text-sm text-brand-muted mb-6">
                  Crea il tuo account personale per acquistare e seguire i corsi.{" "}
                  Sei un'azienda?{" "}
                  <Link to="/registrati/azienda" className="text-brand-blue hover:underline underline-offset-4 inline-flex items-center gap-1">
                    <Building2 size={13} /> Registra un'azienda
                  </Link>
                </p>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-individual-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Nome" value={form.first_name} onChange={set("first_name")} required />
                    <Field label="Cognome" value={form.last_name} onChange={set("last_name")} required />
                  </div>
                  <Field label="Email" type="email" value={form.email} onChange={set("email")} required />
                  <Field label="Password (min. 8 caratteri)" type="password" value={form.password} onChange={set("password")} required minLength={8} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Field
                        label="Codice fiscale"
                        value={form.fiscal_code}
                        onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })}
                        required
                        maxLength={16}
                        placeholder="RSSMRA85M01H501U"
                      />
                      <p className="text-xs text-brand-muted mt-1">Obbligatorio: verrà riportato sull'attestato.</p>
                    </div>
                    <div>
                      <Field label="Data di nascita" type="date" value={form.birth_date} onChange={set("birth_date")} required />
                      <p className="text-xs text-brand-muted mt-1">Obbligatoria: riportata sull'attestato.</p>
                    </div>
                  </div>
                  <Field label="Telefono (facoltativo)" value={form.phone} onChange={set("phone")} />

                  <label className="flex items-start gap-2 text-sm text-brand-muted">
                    <input type="checkbox" checked={form.privacy_accepted}
                      onChange={(e) => setForm({ ...form, privacy_accepted: e.target.checked })}
                      className="mt-0.5" />
                    <span>
                      Ho letto e accetto l'
                      <Link to="/privacy" className="text-brand-blue hover:underline underline-offset-4">informativa privacy</Link>.
                    </span>
                  </label>

                  {error && (
                    <div role="alert" className="flex items-start gap-2 rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <button type="submit" disabled={loading}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed">
                    {loading ? (<><Loader2 size={18} className="animate-spin" /> Registrazione…</>) : "Registrati"}
                  </button>
                </form>
              </>
            )}
          </div>

          <p className="text-xs text-center text-brand-muted mt-6">
            Hai già un account?{" "}
            <Link to="/login" className="text-brand-blue hover:underline underline-offset-4">Accedi</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <Label className="text-sm text-brand-ink">{label}</Label>
      <Input
        {...props}
        className="mt-1.5 h-11 rounded-sm border-brand-line focus-visible:ring-2 focus-visible:ring-brand-blue"
      />
    </div>
  );
}
